import React, { useState, useContext } from "react";
import PropTypes from "prop-types";
import { FormDialog } from "components/common_components/Dialogs";
import { Form as FormikForm, TextField, CheckboxField, SelectField, AutocompleteField } from "components/common_components/Formik";
import FormControl from "@material-ui/core/FormControl";
import FormLabel from "@material-ui/core/FormLabel";
import FormGroup from "@material-ui/core/FormGroup";
import { Grid, Divider } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { taskStatuses } from "utils/static/workflowsStaticData";
import SettingsContext from "../SettingsContext";
import { useDispatch } from "react-redux";
import { notifyError, notifySuccess } from "redux/actions/Notifications";
import { flowValidationSchema, taskValidationSchema } from "./validationSchemas";
import FlowTasksComponent from "./FlowTasks";
import { hasElements } from "utils/arrays";
import { getUnitsOfTime } from "utils/dateUtils";
import Button from "components/common_components/Button";
import Icon from "components/common_components/Icon";

const useStyles = makeStyles((theme) => ({
    formControl: {
        margin: "unset",
        paddingTop: theme.spacing(3),
    },
    formControlTasks: {
        margin: "unset",
        paddingTop: theme.spacing(3),
    },
    autocompleteOption: {
        display: "flex",
        flexDirection: "column",
        padding: 5,
        width: "100%",
    },
    autocompleteOptionUserName: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
    },
    adminFlag: {
        fontSize: "smaller",
        color: "silver",
    },
}));

function Form({ data, resources, mode, onEdit, getFlow, ...rest }) {
    const classes = useStyles();
    const readOnly = mode === "detail";
    const dispatch = useDispatch();
    const { modules, types, users } = resources;
    const { FlowTasks } = useContext(SettingsContext);
    const [formInitialValues, setFormInitialValues] = useState(undefined);
    const [formDialogProps, setFormDialogProps] = useState({
        open: false,
        action: "add",
        maxWidth: "lg",
        fullWidth: true,
        title: "",
        formId: "in-dialog-form",
        isSubmitting: false,
        onClose: () => {
            setFormInitialValues(undefined);
            setFormDialogProps((prev) => ({ ...prev, open: false }));
        },
    });

    const getGuestUsers = () => {
        return hasElements(users) ? users : [];
    };

    function onSuccess(sentPayload, setSubmitting) {
        setSubmitting(false);
        setFormDialogProps((prev) => ({ ...prev, isSubmitting: false }));
        dispatch(notifySuccess(`Task ${formDialogProps.action === "add" ? "created" : "updated"} successfully!`));
        formDialogProps.onClose();
        getFlow();
    }

    function onError(error, setSubmitting, setErrors) {
        if (error && error.errors) {
            setErrors(error.errors);
            dispatch(notifyError(error.message));
        } else {
            dispatch(notifyError(error));
            formDialogProps.onClose();
        }
        setSubmitting(false);
        setFormDialogProps((prev) => ({ ...prev, isSubmitting: false }));
    }

    function normalizeUsersToNotify(notifyBeforeDueDateUsers, defaultAssigneeUser) {
        const mailsTo = [];
        if (hasElements(notifyBeforeDueDateUsers)) {
            const filteredUsers = defaultAssigneeUser
                ? notifyBeforeDueDateUsers.filter((x) => x.id !== defaultAssigneeUser.id)
                : notifyBeforeDueDateUsers;
            mailsTo.push(...filteredUsers.map((x) => x.email));
        }
        return mailsTo;
    }

    async function onSubmitHandler(values, { setSubmitting, setErrors }) {
        const {
            defaultAssigneeUser,
            notifyBeforeDueDate,
            notifyBeforeDueDateInterval,
            notifyBeforeDueDateTimeUnit,
            notifyBeforeDueDateUsers,
            ...payload
        } = values;

        const apiCall = FlowTasks[formDialogProps.action];

        const payloadToSend = {
            ...payload,
            id: formDialogProps.action === "update" ? payload.id : null,
            settingsFlowId: data.id,
            defaultAssigneeUserId: defaultAssigneeUser ? defaultAssigneeUser.id : null,
            ...(notifyBeforeDueDate
                ? {
                      notifyBeforeDueDate: Boolean(notifyBeforeDueDate),
                      notifyBeforeDueDateInterval: Number(notifyBeforeDueDateInterval),
                      notifyBeforeDueDateTimeUnit: String(notifyBeforeDueDateTimeUnit),
                      notifyBeforeDueDateConfig: JSON.stringify({
                          mailsTo: normalizeUsersToNotify(notifyBeforeDueDateUsers, defaultAssigneeUser),
                      }),
                  }
                : {
                      notifyBeforeDueDate: false,
                      notifyBeforeDueDateInterval: null,
                      notifyBeforeDueDateTimeUnit: null,
                      notifyBeforeDueDateConfig: null,
                  }),
        };

        setSubmitting(true);
        setFormDialogProps((prev) => ({ ...prev, isSubmitting: true }));

        apiCall({
            payload: payloadToSend,
            onSuccess: () => onSuccess(payloadToSend, setSubmitting),
            onError: (error) => onError(error, setSubmitting, setErrors),
        });
    }

    function onChangeOrdinalHandler(task, direction) {
        FlowTasks.changeOrdinal({
            payload: {
                id: task.id,
                direction,
            },
            onSuccess: () => {
                dispatch(notifySuccess(`Task moved ${direction} successfully!`));
                getFlow();
            },
            onError: (error) => {
                if (error && error.errors) {
                    console.error(error);
                    dispatch(notifyError(error.message));
                } else {
                    dispatch(notifyError(error));
                }
            },
        });
    }

    return (
        <>
            <FormikForm validateOnChange initialValues={data} validationSchema={flowValidationSchema} showButtons={!readOnly} {...rest}>
                {({ values }) => (
                    <>
                        <div className="layout">
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={4}>
                                    <FormControl component="fieldset" className={classes.formControl}>
                                        <FormLabel component="legend">General Information</FormLabel>
                                        <FormGroup row>
                                            <TextField disabled={readOnly} fieldName="name" label="Name *" multiline minRows={3} />
                                            <TextField
                                                disabled={readOnly}
                                                fieldName="description"
                                                label="Description"
                                                multiline
                                                minRows={10}
                                            />
                                            <AutocompleteField
                                                disabled={readOnly}
                                                fieldName="ownerUser"
                                                label="Owner User *"
                                                options={users}
                                                valueKey="id"
                                                labelKey="name"
                                                renderOption={(option) => (
                                                    <div>
                                                        <div>{`${option.name}${option.isAdmin ? " (admin) " : " "}`}</div>
                                                        <small>
                                                            <strong>{option.email}</strong>
                                                        </small>
                                                    </div>
                                                )}
                                                getOptionLabel={(option) => {
                                                    if (option) {
                                                        return `${option.name}${option.isAdmin ? " (admin)" : ""}`;
                                                    }

                                                    return "";
                                                }}
                                            />
                                        </FormGroup>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <FormControl component="fieldset" className={classes.formControl}>
                                        <FormLabel component="legend">Triggering {"&"} Behavior</FormLabel>
                                        <FormGroup row>
                                            <SelectField
                                                disabled={readOnly}
                                                fieldName="moduleId"
                                                label="Module *"
                                                options={modules.map((m) => ({ value: m.id, label: m.name }))}
                                                margin="normal"
                                            />
                                            <SelectField
                                                disabled={readOnly}
                                                fieldName="typeId"
                                                label="Type *"
                                                options={types
                                                    .filter((x) => x.module.id === values.moduleId)
                                                    .map((m) => ({ value: m.id, label: m.type }))}
                                                margin="normal"
                                            />
                                        </FormGroup>
                                        <FormGroup>
                                            <CheckboxField readOnly={readOnly} fieldName="enabled" label="Is Enabled" />
                                            <CheckboxField readOnly={readOnly} fieldName="isStandard" label="Is Standard" />
                                            {!values.isStandard && (
                                                <CheckboxField readOnly={readOnly} fieldName="allowAutoMode" label="Allow Auto Mode" />
                                            )}
                                            <CheckboxField readOnly={readOnly} fieldName="allowManualMode" label="Allow Manual Mode" />
                                            <CheckboxField readOnly={readOnly} fieldName="allowRestart" label="Allow Restart" />
                                            <CheckboxField
                                                readOnly={readOnly}
                                                fieldName="allowAssigneesReassign"
                                                label="Allow Assignees Reassign"
                                            />
                                        </FormGroup>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} md={5} className={classes.formControlTasks}>
                                    <FlowTasksComponent
                                        tasks={values.tasks || []}
                                        settingsFlow={data}
                                        mode={mode}
                                        setFormInitialValues={setFormInitialValues}
                                        setFormDialogProps={setFormDialogProps}
                                        onChangeOrdinal={onChangeOrdinalHandler}
                                        users={getGuestUsers()}
                                    />
                                </Grid>
                            </Grid>
                        </div>
                        {readOnly && (
                            <div className="button-group">
                                <Button secondary outline onClick={rest.onCancel}>
                                    <Icon name="arrow" size="md" color="secondary" />
                                    <span>Back</span>
                                </Button>
                                <Button secondary onClick={onEdit}>
                                    <Icon name="pencil" size="md" color="white" />
                                    <span>Edit</span>
                                </Button>
                            </div>
                        )}
                    </>
                )}
            </FormikForm>
            <FormDialog {...formDialogProps} readOnly={formDialogProps.action === "detail"}>
                {formInitialValues && (
                    <FormikForm
                        id="in-dialog-form"
                        onSubmit={onSubmitHandler}
                        validateOnChange={false}
                        initialValues={formInitialValues}
                        validationSchema={taskValidationSchema}
                        showButtons={false}
                    >
                        {({ values }) => (
                            <Grid container spacing={3}>
                                <Grid item xs={12} sm={4}>
                                    <FormControl component="fieldset" className={classes.formControl} fullWidth>
                                        <FormLabel component="legend">General Information</FormLabel>
                                        <FormGroup row>
                                            <TextField
                                                disabled={formDialogProps.action === "detail"}
                                                fieldName="name"
                                                label="Name *"
                                                multiline
                                                minRows={3}
                                            />
                                            <TextField
                                                disabled={formDialogProps.action === "detail"}
                                                fieldName="description"
                                                label="Description"
                                                multiline
                                                minRows={10}
                                            />
                                            <AutocompleteField
                                                disabled={formDialogProps.action === "detail"}
                                                fieldName="defaultAssigneeUser"
                                                label="Default Assignee User"
                                                options={users}
                                                valueKey="id"
                                                labelKey="name"
                                                renderOption={(option) => (
                                                    <div>
                                                        <div>{`${option.name}${option.isAdmin ? " (admin) " : " "}`}</div>
                                                        <small>
                                                            <strong>{option.email}</strong>
                                                        </small>
                                                    </div>
                                                )}
                                                getOptionLabel={(option) => {
                                                    if (option) {
                                                        return `${option.name}${option.isAdmin ? " (admin)" : ""}`;
                                                    }

                                                    return "";
                                                }}
                                            />
                                            <SelectField
                                                disabled={formDialogProps.action === "detail"}
                                                fieldName="initialStatus"
                                                label="Initial Status *"
                                                options={taskStatuses}
                                                margin="normal"
                                            />
                                        </FormGroup>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <FormControl component="fieldset" className={classes.formControl} fullWidth>
                                        <FormLabel component="legend">Due Time Calculation</FormLabel>
                                        <FormGroup row>
                                            <TextField
                                                disabled={formDialogProps.action === "detail"}
                                                fieldName="dueUnitQty"
                                                label="Due Unit Qty"
                                                type="number"
                                            />
                                            <SelectField
                                                disabled={formDialogProps.action === "detail"}
                                                fieldName="dueUnit"
                                                label="Due Unit"
                                                options={getUnitsOfTime(true)}
                                            />
                                        </FormGroup>
                                    </FormControl>
                                    <Divider style={{ margin: "30px 0" }} />
                                    <FormControl component="fieldset" className={classes.formControl} fullWidth>
                                        <FormLabel component="legend">Other attributes</FormLabel>
                                        <FormGroup row>
                                            <CheckboxField
                                                readOnly={formDialogProps.action === "detail"}
                                                fieldName="waitForPreviousTask"
                                                label="Wait for previous task"
                                            />

                                            <CheckboxField
                                                readOnly={formDialogProps.action === "detail"}
                                                fieldName="allowAssigneeReassign"
                                                label="Allow Assignee Reassign"
                                            />
                                        </FormGroup>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={5}>
                                    <FormControl component="fieldset" className={classes.formControl} fullWidth>
                                        <FormLabel component="legend">Notifications</FormLabel>
                                        <FormGroup row>
                                            <CheckboxField
                                                readOnly={formDialogProps.action === "detail"}
                                                fieldName="notifyBeforeDueDate"
                                                label="Notify due date"
                                            />
                                            <TextField
                                                disabled={!Boolean(values.notifyBeforeDueDate) || formDialogProps.action === "detail"}
                                                fieldName="notifyBeforeDueDateInterval"
                                                label="Interval *"
                                                type="number"
                                            />
                                            <SelectField
                                                disabled={!Boolean(values.notifyBeforeDueDate) || formDialogProps.action === "detail"}
                                                fieldName="notifyBeforeDueDateTimeUnit"
                                                label="Unit of time *"
                                                options={getUnitsOfTime(true)}
                                            />
                                            <AutocompleteField
                                                fieldName="notifyBeforeDueDateUsers"
                                                disabled={!Boolean(values.notifyBeforeDueDate) || formDialogProps.action === "detail"}
                                                multiple
                                                limitTags={3}
                                                options={getGuestUsers()}
                                                label="Also notify users"
                                                valueKey="id"
                                                labelKey="name"
                                                variant="standard"
                                                renderOption={(option) => (
                                                    <div className={classes.autocompleteOption}>
                                                        <div className={classes.autocompleteOptionUserName}>
                                                            <div>
                                                                <strong>{option.name}</strong>
                                                            </div>
                                                            <small className={classes.adminFlag}>{option.isAdmin ? "admin" : ""}</small>
                                                        </div>
                                                        <small>{option.email}</small>
                                                    </div>
                                                )}
                                                getOptionLabel={(option) => (option ? option.name : "")}
                                            />
                                        </FormGroup>
                                    </FormControl>
                                    <Divider style={{ margin: "20px 0" }} />
                                    <FormControl component="fieldset" className={classes.formControl} fullWidth>
                                        <FormGroup row>
                                            <CheckboxField
                                                readOnly={formDialogProps.action === "detail"}
                                                fieldName="notifyClientOnClose"
                                                label="Notify client on close"
                                            />
                                        </FormGroup>
                                    </FormControl>
                                </Grid>
                            </Grid>
                        )}
                    </FormikForm>
                )}
            </FormDialog>
        </>
    );
}

Form.propTypes = {
    data: PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
        description: PropTypes.string,
        moduleId: PropTypes.number,
        module: PropTypes.shape({
            code: PropTypes.string,
            name: PropTypes.string,
            description: PropTypes.string,
        }),
        typeId: PropTypes.number,
        type: PropTypes.shape({
            code: PropTypes.string,
            type: PropTypes.string,
            module: PropTypes.shape({
                code: PropTypes.string,
                name: PropTypes.string,
                description: PropTypes.string,
            }),
        }),
        ownerUserId: PropTypes.number,
        ownerUser: PropTypes.shape({
            name: PropTypes.string,
            email: PropTypes.string,
            isAdmin: PropTypes.bool,
        }),
        enabled: PropTypes.bool,
        allowAutoMode: PropTypes.bool,
        allowManualMode: PropTypes.bool,
        allowRestart: PropTypes.bool,
        allowAssigneesReassign: PropTypes.bool,
        isStandard: PropTypes.bool,
    }),
    resources: PropTypes.shape({
        modules: PropTypes.arrayOf(
            PropTypes.shape({
                code: PropTypes.string,
                name: PropTypes.string,
                description: PropTypes.string,
            })
        ),
        types: PropTypes.arrayOf(
            PropTypes.shape({
                code: PropTypes.string,
                type: PropTypes.string,
                module: PropTypes.shape({
                    code: PropTypes.string,
                    name: PropTypes.string,
                    description: PropTypes.string,
                }),
            })
        ),
        users: PropTypes.arrayOf(
            PropTypes.shape({
                name: PropTypes.string,
                email: PropTypes.string,
                isAdmin: PropTypes.bool,
            })
        ),
    }),
    mode: PropTypes.oneOf(["add", "detail", "update"]).isRequired,
    onEdit: PropTypes.func,
};

Form.defaultProps = {
    data: {
        name: "",
        description: "",
        moduleId: 0,
        typeId: 0,
        ownerUserId: 0,
        enabled: true,
        allowAutoMode: true,
        allowManualMode: true,
        allowRestart: true,
        allowAssigneesReassign: true,
        isStandard: false,
    },
    resources: {
        modules: [],
        types: [],
        users: [],
    },
    onEdit: () => {},
};

export default Form;
